import { useEffect, useState } from "react";
import { FaArrowLeft, FaChevronRight } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { GetHostStats } from "../../../services/api";
import { secondsToHourMinute } from "../../../utils/helpers";

export default function EarningAndStatistics() {
  const [period, setPeriod] = useState("day");
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState([]);

  const [conversionRate, setConversionRate] = useState();
  const [dollar, setDollar] = useState("");
  const [coin, setCoin] = useState("");

  const navigate = useNavigate();

  useEffect(() => {

    GetHostStats(period)
      .then((res) => {
        setStatsData(res.data);
        setConversionRate(res.data?.conversionValue || 650);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err)
      });

  }, [period]);

  const handleDollarChange = (val) => {
    const value = sanitizeDecimal(val);
    setDollar(value);
    setCoin(value ? (parseFloat(value) * conversionRate).toFixed(2) : "");
  };

  const handleCoinChange = (val) => {
    const value = sanitizeDecimal(val);
    setCoin(value);
    setDollar(value ? (parseFloat(value) / conversionRate).toFixed(2) : "");
  };

  const sanitizeDecimal = (value) => {
    // allow digits + one decimal
    let v = value.replace(/[^0-9.]/g, "");
    const parts = v.split(".");
    if (parts.length > 2) {
      v = parts[0] + "." + parts.slice(1).join("");
    }
    return v;
  };

  function formatDate(dateStr) {
    const date = new Date(dateStr);

    const day = date.getDate();
    const weekday = date.toLocaleString("en-US", { weekday: "long" });
    const month = date.toLocaleString("en-US", { month: "long" });

    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return n + "th";
      switch (n % 10) {
        case 1: return n + "st";
        case 2: return n + "nd";
        case 3: return n + "rd";
        default: return n + "th";
      }
    };

    return `${getOrdinal(day)} ${month}, ${weekday}`;
  }

  function formatDateRange({ from, to }) {

    const getOrdinal = (n) => {
      if (n > 3 && n < 21) return n + "th";
      switch (n % 10) {
        case 1: return n + "st";
        case 2: return n + "nd";
        case 3: return n + "rd";
        default: return n + "th";
      }
    };

    const format = (dateStr) => {
      const date = new Date(dateStr);
      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      return `${getOrdinal(day)} ${month}`;
    };

    return `${format(from)} - ${format(to)}`;
  }

  function formatMonth(dateStr) {
    if (!dateStr) return "";

    const date = new Date(dateStr);
    return date.toLocaleString("en-US", { month: "long" });
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-white">
        Loading...
      </div>
    );
  }

  const Card = ({ title, children, onClick, clickable }) => (
    <div
      onClick={onClick}
      className={`
        bg-[#121212] border border-white/10 rounded-2xl p-4
        flex items-center justify-between
        w-full md:w-[80%] mx-auto
        ${clickable ? "cursor-pointer hover:bg-[#1a1a1a] transition" : ""}
      `}
    >
      <div>
        <p className="font-semibold text-white mb-1">{title}</p>
        {children}
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-end gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={`w-3 rounded ${i === 5 ? "bg-red-500 h-8" : "bg-gray-700 h-6"
                }`}
            />
          ))}
        </div>
        {clickable && <FaChevronRight className="text-gray-500 text-lg" />}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[999] bg-black overflow-y-auto p-4 pb-24 md:pb-32 flex justify-center">
      <div className="w-full max-w-4xl">
        {/* HEADER */}
        <div className="w-full md:w-[80%] mx-auto mb-3 grid grid-cols-[auto_1fr_auto] items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center bg-[#121212] border border-white/10 rounded-full px-3 py-2"
          >
            <FaArrowLeft className="text-white" />
          </button>

          <p className="text-lg sm:text-2xl font-bold text-red-500 text-center">
            Earning And Statistics
          </p>

          <div />
        </div>

        {/* TABS */}
        <div className="flex justify-center">
          <div className="flex bg-[#1a1a1a] p-1 rounded-full mb-4 border border-white/10">
            {["day", "week", "month"].map((tab) => (
              <button
                key={tab}
                onClick={() => setPeriod(tab)}
                className={`px-6 py-2 rounded-full capitalize font-semibold transition ${period === tab ? "bg-red-500 text-white" : "text-gray-400"
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <p className="font-bold text-lg text-center mb-3 text-white">
          {period === "day" ? formatDate(statsData?.from) : period === "week" ? formatDateRange({ from: statsData?.from, to: statsData?.to }) : formatMonth(statsData?.from)}
        </p>

        <div className="space-y-4">
          <Card
            title="Earnings"
          >
            <div className="text-gray-300">
              <p>🟡 {statsData?.earnings?.coins}</p>
              <p>💵 {statsData?.earnings?.dollars}</p>
            </div>
          </Card>

          <Card title="New Followers">
            <p className="text-gray-300">👤 {statsData?.followers?.added}</p>
          </Card>

          <Card title="Call Time">
            <p className="text-gray-300">🕒 {secondsToHourMinute(statsData?.calls?.totalDurationSeconds)}</p>
          </Card>

          {/* CALCULATOR */}
          <div className="bg-[#121212] border border-white/10 rounded-2xl p-4 w-full md:w-[80%] mx-auto">
            <p className="font-semibold mb-3 text-white">
              Earnings Calculator (1$ = {conversionRate} Coins)
            </p>

            <div className="flex gap-3">
              <input
                className="bg-black border border-white/20 text-white rounded-full px-4 py-2 w-full outline-none"
                value={dollar ? `$${dollar}` : ""}
                onChange={(e) => handleDollarChange(e.target.value)}
                placeholder="$ Enter amount"
              />
              <input
                className="bg-black border border-white/20 text-white rounded-full px-4 py-2 w-full outline-none"
                value={coin ? `🟡 ${coin}` : ""}
                onChange={(e) => handleCoinChange(e.target.value)}
                placeholder="🟡 Enter amount"
              />
            </div>
          </div>

          <button
            onClick={() => navigate("/payout-chart")}
            className="w-full md:w-[80%] mx-auto block border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-semibold py-3 rounded-full transition"
          >
            VIEW PAYOUT CHART
          </button>

          <div className="h-4 md:h-8" />
        </div>
      </div>
    </div>
  );
}
